
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/use-toast";

interface DormitoryVisits {
  dormitory: string;
  dormitory_mother: string;
  dormitory_mother_contact: string;
  total_visits: number;
  total_students: number;
}

interface EditDialogProps {
  dormitory: DormitoryVisits;
  onClose: () => void;
  isOpen: boolean;
}

const EditDormitoryDialog = ({ dormitory, onClose, isOpen }: EditDialogProps) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [motherName, setMotherName] = useState(dormitory.dormitory_mother || "");
  const [motherContact, setMotherContact] = useState(dormitory.dormitory_mother_contact || "");

  const updateDormitoryMother = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from("students")
        .update({
          dormitory_mother: motherName,
          dormitory_mother_contact: motherContact,
        })
        .eq("school_id", user?.schoolId)
        .eq("dormitory", dormitory.dormitory);

      if (error) throw error;
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Dormitory mother updated successfully",
      });
      queryClient.invalidateQueries({ queryKey: ["dormitory-mothers"] });
      onClose();
    },
    onError: (error) => {
      console.error("Error updating dormitory mother:", error);
      toast({
        title: "Error",
        description: "Failed to update dormitory mother",
        variant: "destructive",
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateDormitoryMother.mutate();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Edit Dormitory Mother</DialogTitle>
            <DialogDescription>
              Update the dormitory mother details for {dormitory.dormitory}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="mother-name">Mother's Name</Label>
              <Input
                id="mother-name"
                value={motherName}
                onChange={(e) => setMotherName(e.target.value)}
                placeholder="Enter mother's name"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="mother-contact">Contact Number</Label>
              <Input
                id="mother-contact"
                value={motherContact}
                onChange={(e) => setMotherContact(e.target.value)}
                placeholder="Enter contact number"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={updateDormitoryMother.isPending}
            >
              {updateDormitoryMother.isPending ? "Updating..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const DormitoryMotherManager = () => {
  const { user } = useAuth();
  const [selectedDormitory, setSelectedDormitory] = useState<DormitoryVisits | null>(null);

  const { data: dormitoryMothers, isLoading } = useQuery({
    queryKey: ["dormitory-mothers", user?.schoolId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("dormitory_mother_visits")
        .select("*");

      if (error) throw error;
      return data as DormitoryVisits[];
    },
    enabled: !!user?.schoolId,
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Dormitory Mothers</CardTitle>
        <CardDescription>
          Manage dormitory mother assignments and view their statistics
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="text-center">Loading dormitory data...</div>
        ) : dormitoryMothers?.length === 0 ? (
          <div className="text-center text-muted-foreground">
            No dormitories found
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {dormitoryMothers?.map((dormitory) => (
              <Card key={dormitory.dormitory}>
                <CardHeader>
                  <CardTitle className="text-lg">{dormitory.dormitory}</CardTitle>
                  <CardDescription>
                    {dormitory.total_students} students | {dormitory.total_visits} visits
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div>
                      <span className="font-medium">Mother:</span>{" "}
                      {dormitory.dormitory_mother || "Not assigned"}
                    </div>
                    <div>
                      <span className="font-medium">Contact:</span>{" "}
                      {dormitory.dormitory_mother_contact || "Not provided"}
                    </div>
                    <Button
                      className="w-full mt-4"
                      onClick={() => setSelectedDormitory(dormitory)}
                    >
                      Edit Assignment
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </CardContent>
      {selectedDormitory && (
        <EditDormitoryDialog
          dormitory={selectedDormitory}
          isOpen={!!selectedDormitory}
          onClose={() => setSelectedDormitory(null)}
        />
      )}
    </Card>
  );
};
